import {
  type IUseGetSocialOfferRefusedCandidatesResponse,
  displayOrDownloadUrlDocument,
  useDownloadDocument,
} from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { Checkbox, Divider, Stack, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { PARTNER_DECISION_CAUSE } from "@root/utils/constants/fup";
import { useI18nContext } from "@root/utils/organisation";
import { isAuthorize, useAuthorizer } from "@styleguide";
import clsx from "clsx";
import type { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import { RefusedCandidatesTableRow, classes } from "../styles";
import { AuthorChip } from "./authorChip";

interface IPrescriptionCandidatesCardProps {
  data: IUseGetSocialOfferRefusedCandidatesResponse;
  socialApplySelected: ObjectId[];
  setSocialApplySelected: Dispatch<SetStateAction<ObjectId[]>>;
}

export const RefusedCandidatesCard = ({
  data,
  socialApplySelected,
  setSocialApplySelected,
}: IPrescriptionCandidatesCardProps) => {
  const { user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const hasVisualizeCandidatePermission = isAuthorize(user.roleV2, ["candidate.visualize"]);
  const isAuthorizedArchive = useAuthorizer(["offers.social.archive"]);

  const [, downloadDocument] = useDownloadDocument();

  const handleDownloadDocument = async () => {
    const res = await downloadDocument(data.prescription.documentId);
    if (res.status === 200) {
      displayOrDownloadUrlDocument(res.data, "display", data.prescription.name, "application/pdf");
    }
  };

  return (
    <RefusedCandidatesTableRow
      hasVisualizeCandidatePermission={hasVisualizeCandidatePermission}
      isAuthorizedArchive={isAuthorizedArchive}
    >
      <Stack className={classes.root} sx={{ textDecoration: "unset", color: "unset" }}>
        {isAuthorizedArchive && (
          <Checkbox
            size="small"
            color="primary"
            checked={socialApplySelected.includes(data._id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSocialApplySelected((prev) => [...prev, data._id]);
              } else {
                setSocialApplySelected((prev) => prev.filter((id) => id !== data._id));
              }
            }}
          />
        )}
        {hasVisualizeCandidatePermission ? (
          <Stack
            className={clsx(classes.cell, classes.candidate)}
            component={Link}
            to={`/v2/candidate/${data.candidate._id}`}
            target="_blank"
          >
            <Typography
              variant="body1SemiBold"
              sx={{
                color: "primary.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: "underline",
              }}
            >
              {data.candidate.lastName} {data.candidate.firstName}
            </Typography>
          </Stack>
        ) : (
          <Stack className={clsx(classes.cell, classes.candidate)}>
            <Typography variant="body1SemiBold" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {data.candidate.lastName} {data.candidate.firstName}
            </Typography>
          </Stack>
        )}
        <Stack className={clsx(classes.cell, classes.prescription)}>
          {Object.keys(data.prescription).length > 0 ? (
            <Typography
              className={classes.document}
              variant="body1SemiBold"
              color="primary"
              onClick={handleDownloadDocument}
            >
              Fiche.pdf
            </Typography>
          ) : (
            <Typography
              variant="body1"
              sx={{
                color: "black.light",
                fontStyle: "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Prescription manuelle
            </Typography>
          )}
        </Stack>
        <Stack className={clsx(classes.cell, classes.date)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {data.responseDate.toLocaleDateString("fr-FR", { timeZone })}
          </Typography>
        </Stack>
        <Stack className={clsx(classes.cell, classes.cause)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {PARTNER_DECISION_CAUSE[data.cause] ?? "-"}
          </Typography>
        </Stack>
        <Stack className={clsx(classes.cell, classes.author)}>
          <AuthorChip author={data.author} />
        </Stack>
      </Stack>
      <Divider />
    </RefusedCandidatesTableRow>
  );
};
